import {
  STATIC_MODULE_PRIVILEGES,
  STATIC_MODULE_ROUTE_PATHS,
} from "../constants/acMenu";
import type { AcMenuItem } from "../types/acMenu";

/**
 * Normalize a path-like segment to have a leading `/` (or `""` if blank).
 * Used for SPA routes (`/${name}`) and iframe URL path suffixes.
 */
export const normalizeAcMenuPath = (path: string | null | undefined): string => {
  const raw = (path || "").trim();
  if (!raw) return "";
  return raw.startsWith("/") ? raw : `/${raw}`;
};

/** AC SPA route + SideMenu navigate target: `/${module.name}` */
export const toAcMenuRoutePath = (
  name: string | null | undefined
): string => normalizeAcMenuPath(name);

export function buildAcMenuIframeUrl(
  subDomainName: string,
  urlDomain: string,
  path: string | null | undefined,
  currentTenantId: string
): string {
  const suffix = normalizeAcMenuPath(path);
  return `https://${subDomainName}.${urlDomain}${suffix}?currentTenantId=${encodeURIComponent(
    currentTenantId
  )}`;
}

/**
 * Intersection: getAllAcMenus ∩ menuPrivilege.includes(privilege),
 * excluding static module privileges / route paths (`/${name}`) and incomplete rows.
 * API `path` is optional (iframe URL suffix only).
 */
export function filterMatchedAcMenus(
  menus: AcMenuItem[] | null | undefined,
  menuPrivilege: string[]
): AcMenuItem[] {
  if (!menus?.length) return [];
  return menus.filter((item) => {
    const privilege = (item.privilege || "").trim();
    const name = (item.name || "").trim();
    const routePath = toAcMenuRoutePath(name);
    const subDomain = (item.subDomainName || "").trim();
    if (!privilege || !name || !subDomain) {
      if (process.env.NODE_ENV === "development") {
        console.warn("[ac-menu] skipping incomplete item", item);
      }
      return false;
    }
    if (STATIC_MODULE_PRIVILEGES.has(privilege)) return false;
    if (STATIC_MODULE_ROUTE_PATHS.has(routePath)) return false;
    return menuPrivilege.includes(privilege);
  });
}

/** Find privilege for a dynamic AC menu SPA route (`/${name}`). */
export function findDynamicAcMenuPrivilege(
  pathname: string,
  modules: AcMenuItem[]
): string | undefined {
  const normalized = toAcMenuRoutePath(pathname);
  if (!normalized || !modules?.length) return undefined;

  const match = modules.find((item) => {
    const routePath = toAcMenuRoutePath(item.name);
    if (!routePath) return false;
    return normalized === routePath || normalized.startsWith(`${routePath}/`);
  });
  return match?.privilege;
}
